import { useEffect } from "react";

/**
 * Per-venue "add to home screen" naming.
 *
 * CRA ships ONE static `public/manifest.json` for the whole SPA, so without this
 * a guest who adds their café's card to the home screen gets a tile called
 * "POP UP PUSH" — the vendor's name, not the place they actually visit. This
 * swaps in a small generated manifest naming the venue, and sets the iOS
 * `apple-mobile-web-app-title`, which is what Safari uses instead of a manifest.
 *
 * ⚠️ Delivered as a `data:` URI, which Chromium honours and some browsers
 * ignore. That is a deliberate trade: the alternative is a server-rendered
 * `.webmanifest` route, which for a cross-origin API host drags in manifest CORS
 * and a `crossorigin` attribute for a cosmetic win. Where the data: URI is
 * ignored the page simply keeps the static manifest — add-to-home still works,
 * the tile is just named generically. Nothing depends on this succeeding.
 */
export default function useVenueManifest(venueName, venueCode, startUrl) {
  useEffect(() => {
    if (!venueName) return undefined;

    const doc = document;
    const link = doc.querySelector('link[rel="manifest"]');
    const previousHref = link ? link.getAttribute("href") : null;
    const previousTitle = doc.title;

    const manifest = {
      name: `${venueName} — Loyalty`,
      short_name: venueName.slice(0, 12),
      // Whatever page installed it is where launching it should land. Installing
      // from the CARD and being dropped on the menu is a bug the guest reads as
      // "my points are gone" — they added a card and got a takeaway menu.
      start_url: startUrl || (venueCode ? `/v/${venueCode}` : "/"),
      // Scope stays at the root so the installed app can move between the card
      // and the menu without the browser treating it as leaving the app.
      scope: "/",
      display: "standalone",
      background_color: "#f7efe2", // cream
      theme_color: "#150c07",      // espresso
      icons: [{ src: "/popuppush.ico", sizes: "64x64", type: "image/x-icon" }],
    };

    try {
      if (link) {
        link.setAttribute("href", `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`);
      }
      doc.title = `${venueName} — ${doc.title}`;

      let apple = doc.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (!apple) {
        apple = doc.createElement("meta");
        apple.setAttribute("name", "apple-mobile-web-app-title");
        doc.head.appendChild(apple);
      }
      apple.setAttribute("content", venueName);
    } catch (_) {
      /* cosmetic only — never let this break the page */
    }

    // Restore on unmount: this is a SPA, and leaving one venue's manifest
    // attached while the user navigates into the dashboard would be worse than
    // never having set it.
    return () => {
      try {
        if (link && previousHref) link.setAttribute("href", previousHref);
        doc.title = previousTitle;
      } catch (_) {}
    };
  }, [venueName, venueCode, startUrl]);
}
